import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/launchpad";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      navigate(redirectTo, { replace: true });
    } else {
      setError(res.error || "Incorrect email or password");
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen flex grain relative overflow-hidden">
      {/* Layered background: base gradient + amber radial glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#09090b] via-[#0a0a0c] to-[#09090b]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#f5a623]/[0.03] blur-3xl pointer-events-none" />

      <div className="relative z-10 flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo + tagline */}
          <div className="flex flex-col items-center mb-10">
            <img
              alt="Whiplash Film & Creative"
              src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
              className="h-20 w-auto mb-2"
            />
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">
              Project Manager
            </p>
          </div>

          {/* Form card */}
          <form
            onSubmit={onSubmit}
            autoComplete="on"
            className="space-y-5 bg-zinc-950/40 border border-zinc-800/60 rounded-2xl p-8 backdrop-blur-sm"
          >
            <div>
              <h1 className="text-2xl font-heading font-semibold tracking-tight text-zinc-100">
                Welcome back
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Sign in to manage your projects and call sheets.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@whiplash.com"
                className="w-full bg-black/40 border border-zinc-800 rounded-md px-4 py-2.5 text-sm text-zinc-100 focus:outline-none focus:border-[#f5a623]/60 focus:ring-1 focus:ring-[#f5a623]/30 transition"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-zinc-400">
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-black/40 border border-zinc-800 rounded-md px-4 py-2.5 text-sm text-zinc-100 focus:outline-none focus:border-[#f5a623]/60 focus:ring-1 focus:ring-[#f5a623]/30 transition"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting || !email.trim() || !password}
              className="w-full bg-[#f5a623] text-black font-semibold rounded-md py-2.5 text-sm hover:bg-[#f5a623]/90 transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" /> Sign in
                </>
              )}
            </button>

            <div className="text-center pt-1">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs text-zinc-500 hover:text-[#f5a623] transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="text-center text-xs text-zinc-600 mt-6">
            Whiplash Film &amp; Creative · Projects
          </p>
        </div>
      </div>
    </div>
  );
}
